import { ChangelogService } from '../api/services/ChangelogService.js';
import { FeedbackService } from '../api/services/FeedbackService.js';
import { HelpService } from '../api/services/HelpService.js';
import { LiveChatService } from '../api/services/LiveChatService.js';
import { SurveyService } from '../api/services/SurveyService.js';
import { BaseAPIService } from './BaseAPIService.js';
import { getIpInfo } from '../utils/getIpInfo.js';

export class APIService extends BaseAPIService {
	constructor(config = {}) {
		super(config);

		this.feedback = new FeedbackService(this);
		this.survey = new SurveyService(this);
		this.liveChat = new LiveChatService(this);
		this.help = new HelpService(this);
		this.changelog = new ChangelogService(this);
	}

	async submitFeedback(data) {
		return this.feedback.submitFeedback(data);
	}

	async getActiveSurveys(context) {
		return this.survey.getActiveSurveys(context);
	}

	async submitSurveyResponse(surveyId, responseData) {
		return this.survey.submitSurveyResponse(surveyId, responseData);
	}

	async dismissSurvey(surveyId) {
		return this.survey.dismissSurvey(surveyId);
	}

	async getLiveChatSettings() {
		return this.liveChat.getLiveChatSettings();
	}

	async checkAgentsOnline() {
		return this.liveChat.checkAgentsOnline();
	}

	async getConversations(options) {
		return this.liveChat.getConversations(options);
	}

	async getConversation(conversationId) {
		return this.liveChat.getConversation(conversationId);
	}

	async getMessages(conversationId, options) {
		return this.liveChat.getMessages(conversationId, options);
	}

	async startConversation(data) {
		return this.liveChat.startConversation(data);
	}

	async sendMessage(conversationId, data) {
		return this.liveChat.sendMessage(conversationId, data);
	}

	async uploadFile(base64Data, filename) {
		return this.liveChat.uploadFile(base64Data, filename);
	}

	async sendTypingIndicator(conversationId, isTyping) {
		return this.liveChat.sendTypingIndicator(conversationId, isTyping);
	}

	async markConversationAsRead(conversationId) {
		return this.liveChat.markConversationAsRead(conversationId);
	}

	async getUnreadCount() {
		return this.liveChat.getUnreadCount();
	}

	async submitRating(conversationId, data) {
		return this.liveChat.submitRating(conversationId, data);
	}

	async identify(metadata) {
		await this._ensureSession();

		if (this.mock) {
			await new Promise((r) => setTimeout(r, 300));
			const mockResponse = {
				status: true,
				data: {
					contact_id: 'mock_contact_' + Date.now(),
					email: metadata.email || '',
					name: metadata.name || '',
					avatar: metadata.avatar || '',
					is_new: true,
				},
			};
			this._storeContactIdentity(mockResponse.data, metadata);
			return mockResponse;
		}

		const payload = {};

		if (metadata.email) payload.email = metadata.email;
		if (metadata.user_id) payload.user_id = metadata.user_id;
		if (metadata.name) payload.name = metadata.name;
		if (metadata.avatar) payload.avatar = metadata.avatar;
		if (metadata.attributes) payload.attributes = metadata.attributes;
		if (metadata.company) payload.company = metadata.company;

		const ipInfo = await getIpInfo();
		if (ipInfo) payload.ip_info = ipInfo;

		const response = await this._makeRequest('/widget/identify', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${this.sessionToken}`,
			},
			body: JSON.stringify(payload),
		});

		if (response?.status && response?.data) {
			this._storeContactIdentity(response.data, metadata);
		}

		return response;
	}

	_storeContactIdentity(data, metadata = {}) {
		this.contactId = data.contact_id || null;
		this.contactEmail = data.email || metadata.email || null;
		this.contactName = data.name || metadata.name || null;

		try {
			localStorage.setItem(
				'product7_contact',
				JSON.stringify({
					contactId: this.contactId,
					contactEmail: this.contactEmail,
					contactName: this.contactName,
				})
			);
		} catch (e) {
			/* silent */
		}
	}

	getContactIdentity() {
		if (this.contactId) {
			return {
				contactId: this.contactId,
				contactEmail: this.contactEmail,
				contactName: this.contactName,
			};
		}

		try {
			const stored = localStorage.getItem('product7_contact');
			if (stored) {
				const parsed = JSON.parse(stored);
				this.contactId = parsed.contactId;
				this.contactEmail = parsed.contactEmail;
				this.contactName = parsed.contactName;
				return parsed;
			}
		} catch (e) {
			/* silent */
		}

		return null;
	}

	async identifyContact(data) {
		return this.identify(data);
	}

	async getHelpCollections(options) {
		return this.help.getHelpCollections(options);
	}

	async searchHelpArticles(query, options) {
		return this.help.searchHelpArticles(query, options);
	}

	async getChangelogs(options) {
		return this.changelog.getChangelogs(options);
	}
}
